/**
 * ComponentController — manages interactive components received from the agent via RPC.
 *
 * Lit Reactive Controller equivalent of useRinggComponents from React Native.
 * Handles: render_component, send_dynamic_data, send_source_urls, disease search,
 * slash commands, and component response sending.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";

import type { RealtimeRoomAdapter, ConnectionState, SimpleComponentPayload, InteractiveFlowPayload, DiseaseOption, RpcInvocation, EventBus } from "@ringg/core";
import { transformRpcToComponent, formatComponentResponse, formatDiseaseSubmission, formatDiseaseSearch, formatSlashCommand } from "@ringg/core";

type ComponentPayload = SimpleComponentPayload | InteractiveFlowPayload;

interface PendingComponentInfo {
  name: string;
  originalConfig: unknown;
}

export class ComponentController implements ReactiveController {
  host: ReactiveControllerHost;

  private adapter: RealtimeRoomAdapter;
  private eventBus: EventBus;
  private cleanupFns: Array<() => void> = [];
  private connState: ConnectionState = "disconnected";
  private pendingResponses = new Map<string, PendingComponentInfo>();
  private diseaseSearchResolver: ((results: DiseaseOption[]) => void) | null = null;
  private rpcRegistered = false;

  components: ComponentPayload[] = [];
  lastSourceUrl = "";

  constructor(host: ReactiveControllerHost, adapter: RealtimeRoomAdapter, eventBus: EventBus) {
    this.host = host;
    this.adapter = adapter;
    this.eventBus = eventBus;
    host.addController(this);
  }

  hostConnected(): void {
    const unsub = this.adapter.onConnectionStateChange((state) => {
      const prev = this.connState;
      this.connState = state;

      if (state === "connected" && prev !== "connected") {
        this._registerRpcHandlers();
      } else if (state !== "connected" && this.rpcRegistered) {
        this._unregisterRpcHandlers();
      }
    });

    this.cleanupFns.push(unsub);

    this.connState = this.adapter.getConnectionState();
    if (this.connState === "connected") {
      this._registerRpcHandlers();
    }
  }

  hostDisconnected(): void {
    this._unregisterRpcHandlers();
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }

  async respondToComponent(componentId: string, responseData: Record<string, unknown>): Promise<void> {
    const pendingInfo = this.pendingResponses.get(componentId);
    const agentIdentity = this.adapter.getAgentIdentity();

    if (!pendingInfo || !agentIdentity) return;

    const { method, payload } = formatComponentResponse(pendingInfo.name, componentId, responseData, pendingInfo.originalConfig);

    try {
      await this.adapter.performRpc(agentIdentity, method, payload);
      this.pendingResponses.delete(componentId);
    } catch (err) {
      console.error("[ComponentController] Failed to send response:", err);
    }
  }

  searchDiseases(query: string): Promise<DiseaseOption[]> {
    return new Promise((resolve) => {
      const agentIdentity = this.adapter.getAgentIdentity();
      if (!agentIdentity) {
        resolve([]);
        return;
      }

      this.diseaseSearchResolver = resolve;
      const { method, payload } = formatDiseaseSearch(query);

      this.adapter.performRpc(agentIdentity, method, payload).catch((err) => {
        console.error("[ComponentController] Disease search RPC failed:", err);
        this.diseaseSearchResolver = null;
        resolve([]);
      });
    });
  }

  async submitDiseaseWidget(
    componentId: string,
    selections: Array<{
      disease_name: string;
      sub_questions: Array<{ question_id: string; answer: string }>;
    }>,
  ): Promise<void> {
    const agentIdentity = this.adapter.getAgentIdentity();
    if (!agentIdentity) return;

    const { method, payload } = formatDiseaseSubmission(componentId, selections);

    try {
      await this.adapter.performRpc(agentIdentity, method, payload);
    } catch (err) {
      console.error("[ComponentController] Disease submit RPC failed:", err);
    }
  }

  async sendSlashCommand(command: string): Promise<void> {
    const agentIdentity = this.adapter.getAgentIdentity();
    if (!agentIdentity) return;

    const { method, payload } = formatSlashCommand(command);

    try {
      await this.adapter.performRpc(agentIdentity, method, payload);
    } catch (err) {
      console.error("[ComponentController] Slash command RPC failed:", err);
    }
  }

  clearComponents(): void {
    this.components = [];
    this.pendingResponses.clear();
    this.diseaseSearchResolver = null;
    this.lastSourceUrl = "";
    this.host.requestUpdate();
  }

  private _addComponent(component: ComponentPayload): void {
    // Deduplicate by component_id
    const existingIndex = this.components.findIndex((c) => c.component_id === component.component_id);
    if (existingIndex !== -1) {
      const updated = [...this.components];
      updated[existingIndex] = component;
      this.components = updated;
    } else {
      this.components = [...this.components, component];
    }

    if (component.component_type === "calendar_booking") {
      this.eventBus.emit("ringg:calendar_booking", {
        status: "shown",
        componentId: component.component_id,
      });
    }

    this.host.requestUpdate();
  }

  private _registerRpcHandlers(): void {
    this._unregisterRpcHandlers();

    // Source URLs
    this.adapter.registerRpcMethod("send_source_urls", async (data: RpcInvocation) => {
      try {
        const parsed = JSON.parse(data.payload);
        if (Array.isArray(parsed.source_urls)) {
          this.lastSourceUrl = parsed.source_urls.join(", ");
        } else if (parsed.source_url) {
          this.lastSourceUrl = parsed.source_url;
        }
        return JSON.stringify({ status: "received" });
      } catch (e) {
        console.error("[ComponentController] Failed to parse send_source_urls:", e);
        throw new Error("Invalid payload for send_source_urls");
      }
    });

    // Legacy render_component
    this.adapter.registerRpcMethod("render_component", async (data: RpcInvocation) => {
      try {
        const parsed = JSON.parse(data.payload) as ComponentPayload;
        if (parsed.component_type && parsed.component_id) {
          this._addComponent(parsed);
        }
        return JSON.stringify({ status: "received" });
      } catch (e) {
        console.error("[ComponentController] Failed to parse render_component:", e);
        throw new Error("Invalid component payload");
      }
    });

    // New send_dynamic_data
    this.adapter.registerRpcMethod("send_dynamic_data", async (data: RpcInvocation) => {
      try {
        const parsed = JSON.parse(data.payload);
        const componentType = parsed.component_type;

        if (!componentType) {
          return JSON.stringify({ status: "error", message: "No component_type provided" });
        }

        const componentName = componentType.replace("render_component_", "");

        // Disease search results — resolve pending promise
        if (componentName === "disease_search_results") {
          const results = (parsed.results ?? []) as DiseaseOption[];
          if (this.diseaseSearchResolver) {
            this.diseaseSearchResolver(results);
            this.diseaseSearchResolver = null;
          }
          return JSON.stringify({ status: "received" });
        }

        const component = transformRpcToComponent(componentName, parsed);

        if (component) {
          this.pendingResponses.set(component.component_id, {
            name: componentName,
            originalConfig: parsed.component_config,
          });
          this._addComponent(component);
          return JSON.stringify({ status: "received", component_id: component.component_id });
        }

        return JSON.stringify({ status: "error", message: `Unknown component type: ${componentName}` });
      } catch (e) {
        console.error("[ComponentController] Failed to parse send_dynamic_data:", e);
        throw new Error("Invalid send_dynamic_data payload");
      }
    });

    this.rpcRegistered = true;
  }

  private _unregisterRpcHandlers(): void {
    if (!this.rpcRegistered) return;
    try {
      this.adapter.unregisterRpcMethod("send_source_urls");
      this.adapter.unregisterRpcMethod("render_component");
      this.adapter.unregisterRpcMethod("send_dynamic_data");
    } catch {
      // no-op
    }
    this.rpcRegistered = false;
  }
}
